import {
  Box,
  CancelIconButton,
  Checkbox,
  GeneralValidationErrors,
  Grid,
  Stack,
  ValidationErrors,
  getSpacing,
  pn,
  useElementBreakpoints,
} from "@compass/core";
import { DurationInput, Input, Select } from "components";
import {
  LkStudentServiceDisplayField,
  LkStudentServiceDisplayFieldId,
  StudentBulkCreateServicesResource,
  StudentService,
  StudentServiceAdditionalEntry,
  StudentServiceType,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";

export const AdditionalEntry = (props: {
  entry: StudentServiceAdditionalEntry;
  setEntry: (value: StudentServiceAdditionalEntry) => void;
  resource: StudentBulkCreateServicesResource;
  serviceTypes: StudentServiceType[];
  validationErrors: ValidationErrors | string[];
  cancelCreate: () => void;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, largeFieldBreakpoints } = useElementBreakpoints();
  const properties = props.resource.serviceProperties || [];
  const generationValidationErrors = props.validationErrors[pn<StudentService>("generalValidationErrors")];
  const selectedServiceType = props.serviceTypes?.find((x) => x.id === props.entry.serviceTypeId);
  const serviceSubTypes = props.resource.serviceSubTypes?.filter((x) => x.serviceTypeId === props.entry.serviceTypeId);

  const displayFields = props.resource.serviceDisplayFields || [];
  const durationDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Duration);
  const serviceLocationDisplayField = displayFields.find(
    (x) => x.id === LkStudentServiceDisplayFieldId.ServiceLocation
  );
  const referralDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Referral);
  const isServiceDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.IsService);

  const serviceTypeField = () => {
    const propertyName = pn<StudentService>("serviceTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.serviceTypes?.find((x) => x.id === props.entry.serviceTypeId)}
        options={props.serviceTypes}
        groupBy={(option) => (option.isForParent ? "Parent" : "Student")}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEntry(
            produce(props.entry, (draft) => {
              draft.serviceTypeId = value?.id;
              draft.serviceSubTypeId = undefined;
              //draft.parentCount = undefined;
            })
          )
        }
      />
    );
  };

  const serviceSubTypeField = () => {
    const propertyName = pn<StudentService>("serviceSubTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={serviceSubTypes?.find((x) => x.id === props.entry.serviceSubTypeId)}
        options={serviceSubTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEntry(
            produce(props.entry, (draft) => {
              draft.serviceSubTypeId = value?.id;
            })
          )
        }
      />
    );
  };

  const parentCountField = () => {
    const propertyName = pn<StudentService>("parentCount");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        required
        errors={props.validationErrors[propertyName]}
        value={props.entry.parentCount}
        onChange={(e) =>
          props.setEntry(
            produce(props.entry, (draft) => {
              draft.parentCount = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  const durationField = () => {
    const propertyName = pn<StudentService>("durationInMinutes");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DurationInput
        required={durationDisplayField?.required}
        errors={props.validationErrors[propertyName]}
        value={props.entry.durationInMinutes}
        onChange={(value) =>
          props.setEntry(
            produce(props.entry, (draft) => {
              draft.durationInMinutes = value!;
            })
          )
        }
      />
    );
  };

  const collegesField = () => {
    const propertyName = pn<StudentService>("colleges");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        required={serviceLocationDisplayField?.required || selectedServiceType?.isServiceLocationMandatory}
        errors={props.validationErrors[propertyName]}
        value={props.entry.colleges}
        options={props.resource.colleges}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setEntry(
            produce(props.entry, (draft) => {
              draft.colleges = values;
            })
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<StudentService>("schoolId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        required={serviceLocationDisplayField?.required || selectedServiceType?.isServiceLocationMandatory}
        errors={props.validationErrors[propertyName]}
        value={props.resource.schools?.find((x) => x.id === props.entry.schoolId)}
        options={props.resource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEntry(
            produce(props.entry, (draft) => {
              draft.schoolId = value?.id;
            })
          )
        }
      />
    );
  };

  const referralField = () => {
    const propertyName = pn<StudentService>("referral");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Checkbox
        label={property?.displayName}
        checked={props.entry.referral}
        onChange={(e) =>
          props.setEntry(
            produce(props.entry, (draft) => {
              draft.referral = e.target.checked;
            })
          )
        }
      />
    );
  };

  const isServiceField = () => {
    const propertyName = pn<StudentService>("isService");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Checkbox
        label={property?.displayName}
        checked={props.entry.isService}
        onChange={(e) =>
          props.setEntry(
            produce(props.entry, (draft) => {
              draft.isService = e.target.checked;
            })
          )
        }
      />
    );
  };

  return (
    <Stack direction="row" alignItems="center" p={getSpacing("md")}>
      <Box flexGrow={1}>
        <Grid ref={breakpointsRef} container spacing="sm">
          <Grid item xs={smallFieldBreakpoints}>
            {serviceTypeField()}
          </Grid>

          {serviceSubTypes && serviceSubTypes.length > 0 && (
            <Grid item xs={smallFieldBreakpoints}>
              {serviceSubTypeField()}
            </Grid>
          )}

          {selectedServiceType?.isForParent && (
            <Grid item xs={smallFieldBreakpoints}>
              {parentCountField()}
            </Grid>
          )}

          {durationDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {durationField()}
            </Grid>
          )}

          {serviceLocationDisplayField && (
            <>
              {selectedServiceType?.aprStudentService?.isServiceLocationForCollege ? (
                <Grid item xs={largeFieldBreakpoints}>
                  {collegesField()}
                </Grid>
              ) : (
                <Grid item xs={smallFieldBreakpoints}>
                  {schoolField()}
                </Grid>
              )}
            </>
          )}

          {isServiceDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {isServiceField()}
            </Grid>
          )}

          {referralDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {referralField()}
            </Grid>
          )}

          {generationValidationErrors && (
            <Grid item xs={12}>
              <GeneralValidationErrors errors={generationValidationErrors} />
            </Grid>
          )}

          {generationValidationErrors && (
            <Grid item xs={12}>
              <GeneralValidationErrors errors={generationValidationErrors} />
            </Grid>
          )}
        </Grid>
      </Box>

      <CancelIconButton onClick={props.cancelCreate} />
    </Stack>
  );
};
